import {
  UIEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import debounce from 'lodash/debounce';
import { InputActionMeta, MenuListProps, NoticeProps, components } from 'react-select';
import { EvaluationLimits } from '../../util/evaluationContract';
import { reportUnexpected } from '../page-supplier-evaluations/reportUnexpected';
import styles from './app-evaluation-wizard.module.scss';

export type SearchStatus = 'loading' | 'done' | 'failed';

export type SearchPage<T> = { items: T[]; hasMore: boolean };

const SEARCH_DELAY_MS = 300;
const LOAD_MORE_DISTANCE_PX = 48;

const asPage = <T,>(found: T[] | SearchPage<T>): SearchPage<T> =>
  Array.isArray(found) ? { items: found, hasMore: false } : found;

// The shared Selector sets noOptionsMessage after its props spread, so only a component can change the text
export const noOptionsMessage = <Option,>(text: string) => {
  const NoOptionsMessage = (props: NoticeProps<Option, boolean>) => (
    <components.NoOptionsMessage {...props}>{text}</components.NoOptionsMessage>
  );
  return NoOptionsMessage;
};

type MenuPaging = { loadMore: () => void; loadingMoreText: string | null };

const MenuPagingContext = createContext<MenuPaging | null>(null);

export const MenuPagingProvider = MenuPagingContext.Provider;

// Listens to the list's own scroll: react-select's onMenuScrollToBottom misses scrollbar drags, keys and touch
export const PagedMenuList = <Option,>(props: MenuListProps<Option, boolean>) => {
  const paging = useContext(MenuPagingContext);
  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    if (list.scrollHeight - list.scrollTop - list.clientHeight <= LOAD_MORE_DISTANCE_PX) {
      paging?.loadMore();
    }
  };
  return (
    <components.MenuList {...props} innerProps={{ ...props.innerProps, onScroll }}>
      {props.children}
      {paging?.loadingMoreText && <div className={styles.menuFooter}>{paging.loadingMoreText}</div>}
    </components.MenuList>
  );
};

export const useServerSearch = <T,>(
  search: (q: string, page: number) => Promise<T[] | SearchPage<T>>,
) => {
  const [results, setResults] = useState<T[]>([]);
  const [status, setStatus] = useState<SearchStatus>('loading');
  const [input, setInput] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const searchRef = useRef(search);
  searchRef.current = search;
  // The query on screen; null after unmount
  const latest = useRef<string | null>(null);
  // Bumped per query and on unmount, so only pages of the newest query land
  const generation = useRef(0);
  const nextPage = useRef(0);
  const fetchingMore = useRef(false);

  const begin = useCallback((q: string) => {
    latest.current = q;
    generation.current += 1;
    fetchingMore.current = false;
    setLoadingMore(false);
    return generation.current;
  }, []);

  const run = useCallback(
    (q: string) => {
      const asked = begin(q);
      searchRef
        .current(q, 0)
        .then(found => {
          if (generation.current !== asked) return;
          const page = asPage(found);
          nextPage.current = 1;
          setResults(page.items);
          setHasMore(page.hasMore);
          setStatus('done');
        })
        .catch(error => {
          reportUnexpected(error);
          if (generation.current !== asked) return;
          setResults([]);
          setHasMore(false);
          setStatus('failed');
        });
    },
    [begin],
  );
  const runLater = useMemo(() => debounce(run, SEARCH_DELAY_MS), [run]);

  useEffect(() => {
    run('');
    return () => {
      latest.current = null;
      generation.current += 1;
      runLater.cancel();
    };
  }, [run, runLater]);

  const loadMore = useCallback(() => {
    const q = latest.current;
    if (q === null || status !== 'done' || !hasMore || fetchingMore.current) return;
    const asked = generation.current;
    const page = nextPage.current;
    fetchingMore.current = true;
    setLoadingMore(true);
    searchRef
      .current(q, page)
      .then(found => {
        if (generation.current !== asked) return;
        const next = asPage(found);
        nextPage.current = page + 1;
        setResults(current => [...current, ...next.items]);
        setHasMore(next.hasMore);
      })
      .catch(error => {
        reportUnexpected(error);
        if (generation.current !== asked) return;
        setStatus('failed');
      })
      .finally(() => {
        if (generation.current !== asked) return;
        fetchingMore.current = false;
        setLoadingMore(false);
      });
  }, [status, hasMore]);

  const onInputChange = (text: string, meta: InputActionMeta) => {
    if (meta.action === 'input-change') {
      const q = text.trim().slice(0, EvaluationLimits.SearchMaxLength);
      setInput(text);
      setStatus('loading');
      begin(q);
      runLater(q);
      return;
    }
    // react-select empties the box on blur, menu close and a pick: show the first page again
    if (text || latest.current === '') return;
    setInput('');
    setStatus('loading');
    runLater.cancel();
    run('');
  };

  return { results, status, input, onInputChange, loadMore, loadingMore };
};
